import React, { useEffect } from 'react';
import { 
  Phone, CheckCircle, ArrowRight,
  Shield, Award, Users, Target, Clock, Star, Building, Calendar,
  Zap, Eye, Wrench, Compass
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import Logo from '../components/Logo';

const Process = () => {
  useEffect(() => {
    document.title = "Our Proven Process - Ceballos Total Framing | Arizona's Most Trusted Structural Process";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover Ceballos Total Framing\'s refined 6-step luxury process. From exclusive consultation to flawless execution. 25+ years of proven excellence in Paradise Valley and Scottsdale.');
    }
  }, []);

  const processSteps = [
    {
      number: '01',
      title: 'Initial Consultation & Assessment',
      subtitle: 'Discovery Phase',
      description: 'We begin with an in-depth consultation to understand your project vision, structural requirements, and timeline. Our expertise ensures proper coordination with architects and engineers from day one.',
      icon: Target,
      details: [
        'Comprehensive project evaluation',
        'Architect and engineer coordination',
        'Timeline and budget assessment',
        'Material specifications review'
      ]
    },
    {
      number: '02',
      title: 'Detailed Engineering Review',
      subtitle: 'Technical Analysis',
      description: 'Our team conducts thorough structural analysis and coordinates directly with your architects and structural engineers to minimize cost, time, and stress while ensuring optimal execution.',
      icon: Building,
      details: [
        'Structural plan analysis',
        'Load calculation verification',
        'Material optimization',
        'Code compliance review'
      ]
    },
    {
      number: '03',
      title: 'Precision Planning & Scheduling',
      subtitle: 'Project Preparation',
      description: 'We develop a comprehensive project plan with detailed scheduling, material procurement, and coordination with other trades to ensure seamless execution.',
      icon: Calendar,
      details: [
        'Detailed project timeline',
        'Material delivery coordination',
        'Trade coordination',
        'Inspection assistance'
      ]
    },
    {
      number: '04',
      title: 'Material Procurement & Preparation',
      subtitle: 'Quality Assurance',
      description: 'Premium materials are carefully selected and prepared. Every piece of lumber is inspected, and all steel components are pre-fabricated to exact specifications.',
      icon: Wrench,
      details: [
        'Premium material selection',
        'Quality inspection protocols',
        'Pre-fabrication and preparation',
        'Material management'
      ]
    },
    {
      number: '05',
      title: 'Expert Installation & Construction',
      subtitle: 'Precision Execution',
      description: 'Our master craftsmen execute the framing with uncompromising precision. Every connection, every measurement, every detail reflects our commitment to structural excellence.',
      icon: Zap,
      details: [
        'Master-level craftsmanship',
        'Continuous quality monitoring',
        'Daily progress updates',
        'Real-time problem solving'
      ]
    },
    {
      number: '06',
      title: 'Final Inspection & Handover',
      subtitle: 'Quality Completion',
      description: 'Comprehensive final inspection ensures every aspect meets our high standards. Complete documentation and seamless handover to the next phase of your project.',
      icon: CheckCircle,
      details: [
        'Thorough final inspection',
        'Quality documentation',
        'Client walkthrough',
        'Warranty and support'
      ]
    }
  ];

  const qualityFeatures = [
    {
      icon: Shield,
      title: 'Zero Structural Failures',
      description: 'Perfect track record across 25+ completed projects'
    },
    {
      icon: Award,
      title: 'CR-7 Licensed Professional',
      description: 'Fully licensed, bonded, and insured for your protection'
    },
    {
      icon: Clock,
      title: 'On-Time Delivery',
      description: '100% on-schedule completion rate for contracted timelines'
    },
    {
      icon: Star,
      title: 'Premium Materials Only',
      description: 'Exclusively sourced top-grade lumber and steel components'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      
      {/* Architectural Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-72">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/PuriResidenceSymmetryBuilders1.jpg" 
            alt="Process Hero Background - Puri Residence Structural Work"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.5) contrast(1.1)' }}
          />
          <div className="absolute inset-0 bg-gray-900/40"></div>
        </div>

        <div className="relative z-20 text-center text-white px-6 max-w-8xl mx-auto">
          <AnimatedSection delay={200}>
            <div className="text-center">
              <div className="inline-block mb-8">
                
              </div>
              <h1 className="text-5xl md:text-7xl font-light mb-8 text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Proven <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Excellence</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Two decades of refined methodology ensuring structural perfection
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                From Vision to <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Reality</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="space-y-16">
            {processSteps.map((step, index) => (
              <AnimatedSection key={index} delay={400 + index * 200}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  
                  {/* Step Content */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '12px' }}>
                        <step.icon className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-4xl font-light text-amber-400" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {step.number}
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className="text-3xl font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {step.title}
                      </h3>
                      <p className="text-lg text-amber-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {step.subtitle}
                      </p>
                    </div>
                    
                    <p className="text-gray-300 text-lg leading-relaxed mb-8 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {step.description}
                    </p>
                    
                    <ul className="space-y-3">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-center text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                          <CheckCircle className="h-5 w-5 text-amber-400 mr-3 flex-shrink-0" />
                          <span className="font-light">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Step Visual */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="bg-gray-900 p-12 border border-amber-400/20 text-center" style={{ borderRadius: '12px' }}>
                      <div className="w-32 h-32 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-8" style={{ borderRadius: '16px' }}>
                        <step.icon className="h-16 w-16 text-white" />
                      </div>
                      <div className="text-6xl font-light text-amber-400 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {step.number}
                      </div>
                      <h4 className="text-xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {step.subtitle}
                      </h4>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Control Section */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Why Our Process <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Works</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityFeatures.map((feature, index) => (
              <AnimatedSection key={index} delay={400 + index * 200}>
                <div className="bg-gray-800 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 text-center h-full" style={{ borderRadius: '12px' }}>
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '12px' }}>
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {feature.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Engineering Coordination Section */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            <AnimatedSection delay={400}>
              <div className="bg-gray-900 p-12 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '12px' }}>
                    <Compass className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-3xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Engineering Excellence</h3>
                </div>
                
                <p className="text-gray-300 text-lg leading-relaxed mb-8 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Our direct coordination with architects and structural engineers sets us apart. This collaborative approach minimizes cost, reduces timeline, and eliminates client stress through seamless professional coordination.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Direct Professional Coordination</h4>
                      <p className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                        We work directly with your project's architects and structural engineers, ensuring seamless communication and optimal outcomes.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Cost & Time Optimization</h4>
                      <p className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Our collaborative approach prevents costly revisions and delays, keeping your project on schedule and within budget.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Client Peace of Mind</h4>
                      <p className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Professional coordination eliminates client stress by ensuring all technical aspects are handled expertly behind the scenes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="space-y-8">
                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <h4 className="text-2xl font-medium text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Communication Excellence</h4>
                  <div className="space-y-4 text-gray-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <div className="flex justify-between items-center py-3 border-b border-gray-700">
                      <span className="font-light">Daily Progress Updates</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-700">
                      <span className="font-light">Real-Time Issue Resolution</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-700">
                      <span className="font-light">Professional Documentation</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="font-light">Direct Client Access</span>
                      <CheckCircle className="h-5 w-5 text-amber-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <h4 className="text-2xl font-medium text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Project Efficiency</h4>
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div>
                      <div className="text-3xl font-light text-amber-400 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>100%</div>
                      <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>On-Time Completion</div>
                    </div>
                    <div>
                      <div className="text-3xl font-light text-amber-400 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Zero</div>
                      <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Structural Failures</div>
                    </div>
                    <div>
                      <div className="text-3xl font-light text-amber-400 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>25+</div>
                      <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Years Experience</div>
                    </div>
                    <div>
                      <div className="text-3xl font-light text-amber-400 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>25+</div>
                      <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Projects Completed</div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center">
              <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Ready to Experience Our <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Process</span>?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Join Arizona's luxury builders who trust our proven methodology for their most important structural projects.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a 
                  href="/contact"
                  className="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-amber-600 text-white px-12 py-4 transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" style={{ borderRadius: '8px' }}></div>
                  <span className="relative z-10 flex items-center">
                    Start Your Project
                    <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </a>
                
                <a 
                  href="tel:602-448-1723"
                  className="group border border-amber-400/60 text-white px-12 py-4 hover:bg-amber-400/10 transition-all duration-500 backdrop-blur-md shadow-xl text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="flex items-center">
                    Call Today
                    <Phone className="ml-3 h-5 w-5 transform group-hover:scale-110 transition-transform duration-300" />
                  </span>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Process;